import * as repo from '../repository/inventoryRepository';
import { normalizeItemName } from '../normalizeItemName';
import {
  BulkUpsertItemPayload,
  BulkUpsertMode,
  BulkUpsertResult,
  CreateInventoryItemInput,
  InventoryItem,
  InventoryLedgerEntryWithName,
  LedgerFilters,
  RecordMovementInput,
  UpdateInventoryItemInput,
} from '../types/inventory';

function isNonNegativeInteger(n: number): boolean {
  return Number.isInteger(n) && n >= 0;
}

// RPC(create_inventory_item_rpc)는 name을 원문 그대로 저장(btrim 안 함) — trim은 여기서 필수.
// 클라 사전검증(repo 호출 전 즉시 throw): 정규화 후 빈 이름 / 나머지 인자가 음이 아닌 정수가
// 아니면 각각 isInvalidItemNameError·isInvalidInventoryArgumentError(inventoryErrors.ts)로 분기
// 가능한 메시지로 던진다 — RPC가 던지는 것과 동일한 어휘라 Task 2 훅이 출처(클라/서버)와 무관하게
// 같은 predicate로 사용자 메시지를 매핑할 수 있다.
export async function createInventoryItem(input: CreateInventoryItemInput): Promise<InventoryItem> {
  const name = input.name.trim();
  if (normalizeItemName(name) === '') {
    throw new Error('invalid_item_name');
  }
  if (
    !isNonNegativeInteger(input.initialStock) ||
    !isNonNegativeInteger(input.price) ||
    !isNonNegativeInteger(input.lowStockThreshold)
  ) {
    throw new Error('invalid_argument');
  }
  return repo.createItem({ ...input, name });
}

// qty는 Number.isInteger 필수. 입고/출고, 조정-add/subtract는 qty > 0. 조정-set은 qty ≥ 0(0 = 재고를
// 0으로 맞춤, no-op 판정은 RPC가 함). mode는 조정일 때만 repo로 전달 — 그 외는 항상 undefined로
// 강제해(호출측이 실수로 넘겨도) RPC 계약(p_mode 무시·null)과 어긋나지 않게 한다.
export async function recordInventoryMovement(input: RecordMovementInput): Promise<number> {
  if (!Number.isInteger(input.qty)) {
    throw new Error('invalid_argument');
  }
  if (input.type === '조정') {
    if (!input.mode) {
      throw new Error('invalid_argument');
    }
    const minQty = input.mode === 'set' ? 0 : 1;
    if (input.qty < minQty) {
      throw new Error('invalid_argument');
    }
    return repo.recordMovement(input);
  }
  if (input.qty <= 0) {
    throw new Error('invalid_argument');
  }
  return repo.recordMovement({ ...input, mode: undefined });
}

// 상품 정보 수정 — 존재하는 필드만 검증·전달하는 partial patch(codexloop R1 #2 수용, 바뀐
// 필드만 보내 다른 탭이 고친 무관 필드의 stale 되덮기 차단 — 같은 필드 동시 수정은 의도적
// last-write-wins). 검증 자체는 createInventoryItem과 동일(trim 후 정규화 빈 이름 / 음이 아닌
// 정수)·동일 에러 어휘. 빈 patch(전 필드 undefined)는 호출부 버그라 invalid_argument로 거부.
// 서버측 유니크 위반(23505→duplicate 번역은 repo)·CHECK는 DB가 최후 방어.
export async function updateInventoryItem(input: UpdateInventoryItemInput): Promise<void> {
  const patch: UpdateInventoryItemInput = { id: input.id };
  if (input.name !== undefined) {
    const name = input.name.trim();
    if (normalizeItemName(name) === '') {
      throw new Error('invalid_item_name');
    }
    patch.name = name;
  }
  if (input.price !== undefined) {
    if (!isNonNegativeInteger(input.price)) {
      throw new Error('invalid_argument');
    }
    patch.price = input.price;
  }
  if (input.lowStockThreshold !== undefined) {
    if (!isNonNegativeInteger(input.lowStockThreshold)) {
      throw new Error('invalid_argument');
    }
    patch.lowStockThreshold = input.lowStockThreshold;
  }
  if (patch.name === undefined && patch.price === undefined && patch.lowStockThreshold === undefined) {
    throw new Error('invalid_argument');
  }
  return repo.updateItem(patch);
}

export const softDeleteInventoryItem = repo.softDeleteItem;

// 일괄 소프트 삭제 — 빈 배열/비정수 id는 호출부 버그라 invalid_argument로 거부(다른 액션과
// 동일 어휘). 반환값 = 실제 삭제된 개수(repo pass-through).
export async function softDeleteInventoryItems(ids: number[]): Promise<number> {
  if (ids.length === 0 || ids.some((id) => !Number.isInteger(id))) {
    throw new Error('invalid_argument');
  }
  return repo.softDeleteItems(ids);
}

export const fetchInventoryItems = repo.fetchActiveItems;

// PR3: useInventory가 소비하는 overview 조회. repo.getOverview는 인자 검증이 필요 없는
// 단순 조회(윈도우 클램프는 RPC 쪽 책임)라 createInventoryItem/recordInventoryMovement처럼
// 클라 사전검증을 추가하지 않고, fetchInventoryItems와 동일하게 그대로 재노출한다
// — 에러는 repo가 이미 logError+throw로 처리(같은 패턴), 여기서 추가로 매핑하지 않는다.
export const fetchInventoryOverview = repo.getOverview;

// PR6: useInventoryQuickAdd가 소비하는 /order '내 상품' 경량 조회 — fetchInventoryOverview와
// 동일한 무인자 단순 조회라 같은 재노출 패턴(사전검증·에러 매핑 없음, repo가 logError+throw).
export const fetchInventoryQuickAdd = repo.getQuickAdd;

// PR7-2: useInventoryTodayKpis가 소비하는 오늘 입고/출고 집계 — fetchInventoryQuickAdd와 동일한
// 무인자 단순 조회라 같은 재노출 패턴(사전검증·에러 매핑 없음, repo가 logError+throw).
export const fetchInventoryTodayKpis = repo.getTodayKpis;

// PR7-3: InventoryLaunchModal이 소비하는 "재고관리 사용 이력" 존재 판정(소프트 삭제 포함) —
// 같은 무인자 단순 조회 재노출 패턴.
export const hasAnyInventoryItemEver = repo.hasAnyItemEver;

// PR7 필터 날짜 검증 — 형식('YYYY-MM-DD')과 달력 실존(2026-02-29 같은 정규화 함정)을 둘 다
// 통과해야 쓴다. 불합격 필드는 조용히 무시(throw 금지) — 필터는 부가 기능이라 잘못된 값
// 하나가 전체 목록 조회를 죽이면 안 된다. 형식만 검사하면(codexloop PR7 R1 P2) 실존하지
// 않는 날짜가 dateFrom으로는 Postgres timestamptz 파싱 오류(쿼리 전체 실패), dateTo로는
// JS Date 정규화(2/29→3/1)로 의도보다 넓은 범위가 조용히 조회된다.
const KST_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function parseKstDate(value: string): Date | null {
  if (!KST_DATE_RE.test(value)) return null;
  const [y, m, d] = value.split('-').map(Number);
  // Date.UTC(99, …)는 0~99년을 1900년대로 매핑한다(codexloop PR7 R2) — 연도 세그먼트에
  // 2자리(예: '99')를 친 오타가 round-trip 불일치로 조용히 무시되면 UI는 필터 활성인데
  // 서버는 전 행을 돌려주는 거짓 상태가 된다. setUTCFullYear는 연도를 리터럴로 받으므로
  // 그런 날짜도 진짜 그 해(0099년)로 파싱돼 정직한 0건 결과로 수렴한다.
  const dt = new Date(0);
  dt.setUTCFullYear(y, m - 1, d);
  dt.setUTCHours(0, 0, 0, 0);
  // round-trip 대조 — 범위 밖 값은 이월되므로(2/29→3/1), 구성 요소가 입력과 다르면 달력에
  // 없는 날짜다.
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) return null;
  return dt;
}

// dateTo(inclusive)를 exclusive 상한으로 바꾸기 위한 다음날 계산 — parseKstDate가 돌려준
// UTC 자정 앵커에 +24h 후 UTC getter로 추출(KST는 DST가 없고 UTC 산술이라 경계 안전).
// 연도도 4자리 패딩 — 100년 미만 연도(위 2자리 오타 경로)가 '99-01-01' 같은 문자열이 되면
// Postgres가 다른 포맷으로 오파싱하거나 거부한다.
function nextDateOf(parsed: Date): string {
  const next = new Date(parsed.getTime() + 24 * 60 * 60 * 1000);
  const yyyy = String(next.getUTCFullYear()).padStart(4, '0');
  const mm = String(next.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(next.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

// 원장 페이지 조회 후 상품명을 머지해 반환(FK 없는 구조라 embed 불가 — 2차 쿼리 + 클라 머지).
// beforeId/limit은 그대로 repo.fetchLedgerPage로 pass-through(id keyset — codexloop R2 Fix 1).
// filters(PR7, optional·additive)는 KST 달력 날짜를 timestamptz 경계로 변환해 얹는다:
// dateFrom → 그 날 00:00(+09:00) inclusive, dateTo → 다음날 00:00(+09:00) exclusive —
// created_at은 timestamptz라 명시 오프셋 ISO 문자열이 그대로 정확한 비교 경계가 된다.
// 이름 조회 자체가 실패하면(네트워크 등) 그대로 throw해 호출부(useInventoryLedger)의 기존
// catch/toast 경로를 탄다. 매칭 실패(존재하지 않는 item_id 등 이론상 케이스)만 item_name: null.
export async function fetchInventoryLedgerPage(
  params: { beforeId?: number; limit: number; filters?: LedgerFilters },
): Promise<InventoryLedgerEntryWithName[]> {
  const { filters, ...pageParams } = params;
  const repoParams: Parameters<typeof repo.fetchLedgerPage>[0] = { ...pageParams };
  if (filters) {
    if (filters.itemId !== null) repoParams.itemId = filters.itemId;
    if (filters.reason !== null) repoParams.reason = filters.reason;
    if (filters.dateFrom !== null && parseKstDate(filters.dateFrom) !== null) {
      repoParams.createdFrom = `${filters.dateFrom}T00:00:00+09:00`;
    }
    if (filters.dateTo !== null) {
      const parsedTo = parseKstDate(filters.dateTo);
      if (parsedTo !== null) {
        repoParams.createdBefore = `${nextDateOf(parsedTo)}T00:00:00+09:00`;
      }
    }
  }
  const page = await repo.fetchLedgerPage(repoParams);
  const itemIds = Array.from(new Set(page.map((entry) => entry.item_id)));
  const names = await repo.fetchItemNamesByIds(itemIds);
  const nameById = new Map(names.map((item) => [item.id, item.name]));
  return page.map((entry) => ({ ...entry, item_name: nameById.get(entry.item_id) ?? null }));
}

// PR5: bulk_upsert_inventory_items_rpc. items는 모달(T3)이 extractInventoryRows 결과(rowNo strip
// 후)로 이미 클라 검증을 마친 행뿐 — 서버 검증(캡·중복·값)은 RPC의 최후 방어선이라 여기서 재검증을
// 반복하지 않는다(과잉 방어 금지, RPC가 이미 전량 검증+전량 롤백). 빈 배열만은 RPC가 P0011로
// 거부하기 전에 명확한 클라 에러로 조기 차단 — 모달이 실행 버튼을 유효 행 0개일 때 이미 비활성화
// 하지만, 이 서비스 함수 자체가 단독 호출될 가능성(방어심층)까지 대비한다.
export async function bulkUpsertInventoryItems(
  items: BulkUpsertItemPayload[],
  mode: BulkUpsertMode,
): Promise<BulkUpsertResult> {
  if (items.length === 0) {
    throw new Error('invalid_argument');
  }
  return repo.bulkUpsertItems(items, mode);
}

// ② AI 헤더 매핑 — 뮤테이션이 아니라(useInventoryActions의 isSubmitting/onMutated 계약과 무관)
// 모달이 이 서비스 함수를 직접 호출한다(hooks-only-import 규율은 훅에 적용되는 것이지, 이 호출은
// repo.requestHeaderMapping이 이미 throw하지 않는 계약이라 그대로 재노출).
export const requestInventoryHeaderMapping = repo.requestHeaderMapping;
